import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CloseIcon } from './icons/CloseIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { upscaleVideo, relightVideo, generateLipsync, editVideoAdvanced, changeVideoVoice, changeVideoVoiceSegment, detectVideoObjects, isDetectEndpointMissingError } from '../services/aiService';
import { uploadDataUriToBackend, ensurePublicVideoUrl, isPublicHttpsUrl } from '../services/backendService';
import { UserAssetsModal } from './wizard/UserAssetsModal';
import { ProjectAssetsPickerModal } from './ProjectAssetsPickerModal';
import { buildAdvancedEditByteplusAssets, buildSwapItems, buildSwapRequest, buildDetectedSwapItems } from '../services/videoPayloadBuilder';
import { SwapObjectsPanel } from './SwapObjectsPanel';
import { EditModelSettings } from './EditModelSettings';
import { VideoAssetIdField } from './VideoAssetIdField';
import { resolveVideoAssetIdForRequest, friendlyEditError } from '../utils/byteplusVideoAsset';

// ─── SVG Icons ───
const SwapIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M7 4L3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7" />
    </svg>
);
const ZoomIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
    </svg>
);
const CropIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 2v4h12v12h4M2 6h4m0 0v16m12-4h4m-4 0V6" />
    </svg>
);
const AdvancedEditIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 3v3m0 12v3M3 12h3m12 0h3M7.8 7.8L5.6 5.6m12.8 12.8l-2.2-2.2m0-9.2l2.2-2.2M5.6 18.4l2.2-2.2" />
        <circle cx="12" cy="12" r="4" />
    </svg>
);
const PlusCircleIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" /><path d="M12 8v8M8 12h8" />
    </svg>
);
const TrashIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" />
    </svg>
);
const LibraryIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);
const EraseIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 20H9L4 15l10-10 7 7-6 6" /><path d="M14 5l5 5" />
    </svg>
);
const UpscaleIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
    </svg>
);
const RelightIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
    </svg>
);
const LipsyncIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
    </svg>
);
const VoiceChangerIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
    </svg>
);
const TrimIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 4v16M18 4v16M6 8h12M6 16h12" />
    </svg>
);
const PlayIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
);
const PauseIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6zM14 4h4v16h-4z" /></svg>
);

const EDIT_TOOLS = [
    { id: 'advancedEdit', name: 'Advanced Edit', icon: AdvancedEditIcon },
    { id: 'swapObjects', name: 'Swap Objects & Characters', icon: SwapIcon },
    { id: 'trim', name: 'Trim', icon: TrimIcon },
    { id: 'zoom', name: 'Zoom & Pan', icon: ZoomIcon },
    { id: 'crop', name: 'Crop', icon: CropIcon },
    { id: 'eraseElement', name: 'Erase Element', icon: EraseIcon },
    { id: 'upscale', name: 'Upscale', icon: UpscaleIcon },
    { id: 'relight', name: 'Relight', icon: RelightIcon },
    { id: 'lipsync', name: 'Lip Sync', icon: LipsyncIcon },
    { id: 'voiceChanger', name: 'Voice Changer', icon: VoiceChangerIcon },
];

const LIGHTING_PRESETS = [
    { id: 'top', label: 'Top Light', prompt: 'Move the main lighting to come from directly above, creating top-down illumination with shadows underneath' },
    { id: 'left', label: 'Left Key', prompt: 'Move the main key light to the left side, creating dramatic side lighting from the left' },
    { id: 'right', label: 'Right Key', prompt: 'Move the main key light to the right side, creating dramatic side lighting from the right' },
    { id: 'back', label: 'Backlight', prompt: 'Add strong backlighting/rim light from behind the subject, creating a silhouette edge glow' },
    { id: 'warm', label: 'Warm Tone', prompt: 'Change the lighting to warm golden-hour tones, with orange and amber color temperature' },
    { id: 'cool', label: 'Cool Tone', prompt: 'Change the lighting to cool blue tones, with a moonlit or twilight atmosphere' },
    { id: 'dramatic', label: 'Dramatic', prompt: 'Create dramatic chiaroscuro lighting with strong contrast between highlights and deep shadows' },
    { id: 'soft', label: 'Soft/Diffused', prompt: 'Make the lighting soft and diffused, reducing harsh shadows for an even, flattering look' },
];

export const VideoEditorModal = ({
    shot,
    projectData,
    onClose,
    onSave,
    onDeductCredits,
    onTrackCreditsUsed,
    onUpdateShotFields,
    onSaveMedia
}) => {
    const [activeTool, setActiveTool] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);
    const compareVideoRef = useRef(null);
    const resultVideoRef = useRef(null);

    // Original tool states
    const [zoomLevel, setZoomLevel] = useState(100);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [cropRegion, setCropRegion] = useState({ x: 0, y: 0, width: 100, height: 100 });
    const [editPrompt, setEditPrompt] = useState('');

    // New tool states
    const [relightPreset, setRelightPreset] = useState(null);
    const [relightCustom, setRelightCustom] = useState('');
    const [lipsyncAudioFile, setLipsyncAudioFile] = useState(null);
    const [lipsyncAudioUrl, setLipsyncAudioUrl] = useState('');
    const [resultVideoUrl, setResultVideoUrl] = useState(null);

    // Advanced Edit states
    const [advancedElements, setAdvancedElements] = useState([]);
    const [advancedPrompt, setAdvancedPrompt] = useState('');
    const [assetPickerForElementId, setAssetPickerForElementId] = useState(null);
    const advancedFileInputRefs = useRef({});

    // Voice Changer states
    const [selectedVoiceCharId, setSelectedVoiceCharId] = useState(null);
    const [voiceSegments, setVoiceSegments] = useState([]);
    const [segmentPreviewTime, setSegmentPreviewTime] = useState(null);

    // Trim states (Nov 2026) — metadata-only trim.
    // Persists as `shot.trimStart` / `shot.trimEnd` (seconds). Downstream
    // stitching/export flows honor these when set. `null` = untrimmed.
    const [trimStart, setTrimStart] = useState(
        typeof shot?.trimStart === 'number' ? shot.trimStart : 0
    );
    const [trimEnd, setTrimEnd] = useState(
        typeof shot?.trimEnd === 'number' ? shot.trimEnd : null
    );

    // Same speech duration estimation as VideoPreProductionModal
    const estimateSpeechDuration = (text) => {
        if (!text || !text.trim()) return 0;
        const wordCount = text.trim().split(/\s+/).length;
        const charCount = text.trim().length;
        const wordEstimate = Math.ceil(wordCount / 2.5);
        const charEstimate = Math.ceil(charCount * 0.08);
        return Math.max(2, Math.max(wordEstimate, charEstimate) + 1);
    };

    // Build voice segments — recalculate timecodes using speech estimation (same as Pre-Production)
    // Group by character, place sequentially
    useEffect(() => {
        const dialogues = shot?.dialogues || [];
        const characters = projectData?.characters || [];

        // Collect dialogue text per character (preserving order of first appearance)
        const charOrder = [];
        const charTextMap = new Map();

        if (dialogues.length > 0) {
            for (const d of dialogues) {
                const char = characters.find(c => c.id === d.characterId || c.name === d.characterName);
                const key = d.characterId || char?.id || d.characterName || 'unknown';
                if (!charTextMap.has(key)) {
                    charOrder.push({
                        characterId: key,
                        characterName: d.characterName || char?.name || 'Speaker',
                        voiceId: char?.voiceId || char?.elevenLabsVoiceId || char?.voice_id || null,
                        image: char?.image || null,
                    });
                    charTextMap.set(key, d.text || '');
                } else {
                    charTextMap.set(key, charTextMap.get(key) + ' ' + (d.text || ''));
                }
            }
        } else {
            // Fallback: build from shot.characters
            const shotChars = (shot?.characters || []).map(cId => characters.find(c => c.id === cId || c.name === cId)).filter(Boolean);
            for (const c of shotChars) {
                if (c.category === 'element' || c.category === 'narrator') continue;
                charOrder.push({
                    characterId: c.id,
                    characterName: c.name,
                    voiceId: c.voiceId || c.elevenLabsVoiceId || c.voice_id || null,
                    image: c.image,
                });
                charTextMap.set(c.id, '');
            }
        }

        // Calculate timecodes sequentially using speech estimation
        let cursor = 0;
        const segments = charOrder.map(info => {
            const text = charTextMap.get(info.characterId) || '';
            const dur = text ? estimateSpeechDuration(text) : Math.max(2, (duration || 5) / Math.max(1, charOrder.length));
            const startTime = cursor;
            const endTime = cursor + dur;
            cursor = endTime;
            return { ...info, startTime, endTime, text, applied: false };
        });

        setVoiceSegments(segments);
    }, [shot?.id, duration]); // eslint-disable-line react-hooks/exhaustive-deps

    const lipsyncAudioInputRef = useRef(null);

    useEffect(() => {
        // Capture the element: videoRef.current is null after unmount, but late events can still fire.
        const v = videoRef.current;
        if (!v) return;
        const onMeta = () => {
            setDuration(v.duration);
            // Seed trimEnd once metadata is available (idempotent).
            setTrimEnd(prev => (prev == null ? v.duration : prev));
        };
        const onTime = () => setCurrentTime(v.currentTime);
        v.addEventListener('loadedmetadata', onMeta);
        v.addEventListener('timeupdate', onTime);
        return () => {
            v.removeEventListener('loadedmetadata', onMeta);
            v.removeEventListener('timeupdate', onTime);
        };
    }, []);

    // AI endpoints need a public https URL; uploaded videos may be blob:/data: — upload once and reuse.
    const publicVideoRef = useRef({ src: null, url: null });
    const videoAssetRecordRef = useRef(null);
    const getPublicVideoUrl = useCallback(async () => {
        if (publicVideoRef.current.src === shot.video && publicVideoRef.current.url) return publicVideoRef.current.url;
        const saved = shot.byteplusVideoAsset;
        if (saved?.videoUrl && saved.sourceVideo === shot.video) {
            publicVideoRef.current = { src: shot.video, url: saved.videoUrl };
            return saved.videoUrl;
        }
        const url = await ensurePublicVideoUrl(shot.video, (m) => { setStatusMsg(m); setDetectState(s => (s.busy ? { ...s, message: m } : s)); });
        publicVideoRef.current = { src: shot.video, url };
        const rec = videoAssetRecordRef.current;
        if (rec && rec.videoUrl !== url && onUpdateShotFields) {
            const next = { ...rec, videoUrl: url };
            videoAssetRecordRef.current = next;
            onUpdateShotFields(shot.id, { byteplusVideoAsset: next });
        }
        return url;
    }, [shot?.video]); // eslint-disable-line react-hooks/exhaustive-deps

    // Trim playback lock: when the Trim tool is active, playback stays inside
    // [trimStart, trimEnd] and loops within that range so users preview
    // exactly what will be kept.
    useEffect(() => {
        if (activeTool !== 'trim') return;
        const v = videoRef.current;
        if (!v) return;
        const onTimeUpdate = () => {
            if (trimEnd != null && v.currentTime >= trimEnd - 0.05) {
                v.currentTime = trimStart;
            } else if (v.currentTime < trimStart) {
                v.currentTime = trimStart;
            }
        };
        v.addEventListener('timeupdate', onTimeUpdate);
        // Snap the playhead into range when the tool activates.
        if (v.currentTime < trimStart || (trimEnd != null && v.currentTime > trimEnd)) {
            v.currentTime = trimStart;
        }
        return () => v.removeEventListener('timeupdate', onTimeUpdate);
    }, [activeTool, trimStart, trimEnd]);

    const handlePlayPause = () => {
        if (!videoRef.current) return;
        if (isPlaying) videoRef.current.pause(); else videoRef.current.play();
        setIsPlaying(!isPlaying);
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const time = ((e.clientX - rect.left) / rect.width) * duration;
        if (videoRef.current) { videoRef.current.currentTime = time; setCurrentTime(time); }
    };

    const formatTime = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

    const trackCredits = (result) => {
        if (onTrackCreditsUsed && result?.creditsUsed) onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
    };

    const fileToDataUrl = (file) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
    });

    // ─── Advanced Edit Helpers ───
    const addAdvancedElement = useCallback(() => {
        setAdvancedElements(prev => [...prev, { id: Date.now(), images: [], description: '' }]);
    }, []);

    const removeAdvancedElement = useCallback((id) => {
        setAdvancedElements(prev => {
            const el = prev.find(e => e.id === id);
            if (el) el.images.forEach(img => { if (img.previewUrl && img.file) URL.revokeObjectURL(img.previewUrl); });
            return prev.filter(e => e.id !== id);
        });
    }, []);

    const updateAdvancedElement = useCallback((id, field, value) => {
        setAdvancedElements(prev => prev.map(el => el.id === id ? { ...el, [field]: value } : el));
    }, []);

    const handleAdvancedFileSelect = useCallback((id, files) => {
        if (!files || files.length === 0) return;
        const newImages = Array.from(files).map(file => ({
            file,
            previewUrl: URL.createObjectURL(file)
        }));
        setAdvancedElements(prev => prev.map(el => {
            if (el.id === id) return { ...el, images: [...el.images, ...newImages] };
            return el;
        }));
    }, []);

    const removeAdvancedImage = useCallback((elementId, imgIdx) => {
        setAdvancedElements(prev => prev.map(el => {
            if (el.id === elementId) {
                const img = el.images[imgIdx];
                if (img?.file && img.previewUrl) URL.revokeObjectURL(img.previewUrl);
                return { ...el, images: el.images.filter((_, i) => i !== imgIdx) };
            }
            return el;
        }));
    }, []);

    const handleAssetSelected = useCallback((url) => {
        if (!assetPickerForElementId || !url) return;
        setAdvancedElements(prev => prev.map(el => {
            if (el.id === assetPickerForElementId) {
                return { ...el, images: [...el.images, { file: null, previewUrl: url }] };
            }
            return el;
        }));
        setAssetPickerForElementId(null);
    }, [assetPickerForElementId]);

    const [projectPickerForElementId, setProjectPickerForElementId] = useState(null);
    const [swapReplacements, setSwapReplacements] = useState({});
    const [swapNote, setSwapNote] = useState('');
    const [editModel, setEditModel] = useState('');
    const [editResolution, setEditResolution] = useState('');
    const [videoAssetRecord, setVideoAssetRecord] = useState(() => {
        const r = shot?.byteplusVideoAsset;
        return r && (!r.sourceVideo || r.sourceVideo === shot?.video) ? r : null;
    });
    videoAssetRecordRef.current = videoAssetRecord;
    const saveVideoAssetRecord = useCallback((rec) => {
        const knownUrl = publicVideoRef.current.src === shot.video ? publicVideoRef.current.url : null;
        const next = rec ? {
            ...videoAssetRecord, ...rec, sourceVideo: shot.video,
            videoUrl: knownUrl || videoAssetRecord?.videoUrl || (isPublicHttpsUrl(shot.video) ? shot.video : null),
        } : null;
        setVideoAssetRecord(next);
        videoAssetRecordRef.current = next;
        if (onUpdateShotFields) onUpdateShotFields(shot.id, { byteplusVideoAsset: next });
    }, [videoAssetRecord, shot?.id, shot?.video, onUpdateShotFields]);
    const videoAssetIdForRequest = resolveVideoAssetIdForRequest(videoAssetRecord, shot?.video);
    const editOptions = { modelKey: editModel || undefined, resolution: editResolution || undefined, videoAssetId: videoAssetIdForRequest || undefined };
    const videoAssetField = <VideoAssetIdField record={videoAssetRecord} onSave={saveVideoAssetRecord} disabled={isProcessing} />;
    const detectCacheKey = shot?.video ? `swapDetect:v1:${shot.video}` : null;
    const [detectedObjects, setDetectedObjects] = useState(() => {
        try { return detectCacheKey ? JSON.parse(localStorage.getItem(detectCacheKey) || 'null') : null; } catch { return null; }
    });
    const [detectState, setDetectState] = useState({ busy: false, message: '', notice: '' });
    const swapSource = detectedObjects?.length ? 'detected' : 'cast';
    const swapItems = React.useMemo(() => (swapSource === 'detected'
        ? buildDetectedSwapItems(detectedObjects, projectData?.characters || [])
        : buildSwapItems(shot, projectData?.characters || [])), [swapSource, detectedObjects, shot, projectData?.characters]);

    // Drop / restore a detected screenshot (e.g. it shows a face) → only the description is sent.
    const showCompare = !!resultVideoUrl && activeTool === 'swapObjects';
    // Keep the edited video in lock-step with the original's play / pause / seek.
    useEffect(() => {
        const main = videoRef.current;
        const cmp = compareVideoRef.current;
        if (!showCompare || !main || !cmp) return;
        const onPlay = () => { cmp.currentTime = main.currentTime; cmp.play().catch(() => {}); };
        const onPause = () => cmp.pause();
        const onSeeked = () => { cmp.currentTime = main.currentTime; };
        main.addEventListener('play', onPlay);
        main.addEventListener('pause', onPause);
        main.addEventListener('seeked', onSeeked);
        return () => {
            main.removeEventListener('play', onPlay);
            main.removeEventListener('pause', onPause);
            main.removeEventListener('seeked', onSeeked);
        };
    }, [showCompare, resultVideoUrl]);

    const handleToggleScreenshot = useCallback((detectedId) => {
        setDetectedObjects(prev => {
            const next = (prev || []).map(d => (d.id === detectedId ? { ...d, screenshotRemoved: !d.screenshotRemoved } : d));
            try { if (detectCacheKey) localStorage.setItem(detectCacheKey, JSON.stringify(next)); } catch { /* quota */ }
            return next;
        });
    }, [detectCacheKey]);

    const handleAnalyseVideo = useCallback(async () => {
        setDetectState({ busy: true, message: 'Submitting video for analysis…', notice: '' });
        try {
            const knownCharacters = (projectData?.characters || []).filter(c => c && c.category !== 'element')
                .map(c => ({ id: c.id, name: c.name, description: c.description || '', image_url: c.image || null }));
            const { items, creditsUsed } = await detectVideoObjects({
                videoUrl: await getPublicVideoUrl(), knownCharacters, projectId: projectData?.id,
                onProgress: (m) => setDetectState(s => ({ ...s, message: m })),
            });
            if (onTrackCreditsUsed && creditsUsed) onTrackCreditsUsed(creditsUsed);
            if (!items.length) {
                setDetectState({ busy: false, message: '', notice: 'Nothing was detected in this video — showing Cast & World items instead.' });
                return;
            }
            setDetectedObjects(items);
            setSwapReplacements({});
            try { localStorage.setItem(detectCacheKey, JSON.stringify(items)); } catch { /* quota */ }
            setDetectState({ busy: false, message: '', notice: '' });
        } catch (e) {
            setDetectState({
                busy: false, message: '',
                notice: isDetectEndpointMissingError(e)
                    ? 'Video analysis is not available yet on the backend — showing Cast & World items instead.'
                    : `Video analysis failed: ${e.message}. Showing Cast & World items instead.`,
            });
        }
    }, [shot?.video, projectData?.characters, projectData?.id, detectCacheKey, onTrackCreditsUsed, getPublicVideoUrl]);
    const handleProjectAssetSelected = useCallback((item) => {
        if (!projectPickerForElementId || !item?.url) return;
        setAdvancedElements(prev => prev.map(el => el.id === projectPickerForElementId
            ? { ...el, images: [...el.images, { file: null, previewUrl: item.url, project: item }] }
            : el));
        setProjectPickerForElementId(null);
    }, [projectPickerForElementId]);

    // ─── Apply Handlers ───
    const handleApplyEdit = async () => {
        if (!activeTool) return;
        setIsProcessing(true); setError(null); setStatusMsg('');

        try {
            switch (activeTool) {
                case 'trim': {
                    // Nov 2026 — HARD trim via ffmpeg.wasm.
                    // Physically cuts the .mp4 in the browser, uploads the
                    // shorter clip to GCS via the existing upload flow, and
                    // updates `shot.video` to the new URL. `shot.videoDuration`
                    // is set to the new (shorter) duration. The metadata
                    // `trimStart`/`trimEnd` fields are cleared since the raw
                    // file itself is now the trimmed one — no downstream
                    // consumer needs to slice.
                    if (trimEnd == null || trimEnd <= trimStart) {
                        setError('Trim end must be after trim start.');
                        break;
                    }
                    const minSpan = 0.5;
                    if (trimEnd - trimStart < minSpan) {
                        setError(`Trimmed clip must be at least ${minSpan}s long.`);
                        break;
                    }
                    const totalDur = duration || 0;
                    // If user hasn't actually trimmed anything (full-length),
                    // don't waste time on a re-encode.
                    if (trimStart <= 0.05 && Math.abs(trimEnd - totalDur) < 0.1) {
                        setError('Nothing to trim — the range covers the whole video.');
                        break;
                    }
                    if (!shot?.video) {
                        setError('No video URL on this shot.');
                        break;
                    }
                    try {
                        // Lazy-load the trim service so ffmpeg.wasm (~30 MB
                        // core) is only fetched when the user actually trims.
                        const { hardTrimVideo } = await import('../services/videoTrimService');
                        setStatusMsg('Loading trim engine…');
                        const result = await hardTrimVideo({
                            videoUrl: shot.video,
                            startS: trimStart,
                            endS: trimEnd,
                            projectId: projectData?.id,
                            onProgress: ({ phase, percent }) => {
                                const labelByPhase = {
                                    'downloading-core': 'Loading trim engine…',
                                    'initializing': 'Initializing trim engine…',
                                    'ready': 'Ready. Fetching source video…',
                                    'fetching-source': 'Fetching source video…',
                                    'writing': 'Preparing video…',
                                    'trimming': 'Trimming video…',
                                    'reading': 'Packaging trimmed clip…',
                                    'uploading': 'Uploading trimmed clip…',
                                    'done': 'Done.',
                                };
                                setStatusMsg(`${labelByPhase[phase] || phase} (${percent}%)`);
                            },
                        });
                        if (onSave) {
                            const rounded = (n) => Math.round(n * 100) / 100;
                            onSave({
                                ...shot,
                                video: result.publicUrl,
                                videoDuration: rounded(result.durationS),
                                // Clear metadata trim — the file itself is
                                // now the trimmed clip. Retaining them would
                                // double-trim during preview/export.
                                trimStart: null,
                                trimEnd: null,
                            });
                        }
                        setStatusMsg(`Trim applied. New clip is ${result.durationS.toFixed(1)}s.`);
                        setTimeout(() => setStatusMsg(''), 2500);
                    } catch (e) {
                        console.error('Hard trim failed:', e);
                        setError(`Trim failed: ${e.message || e}`);
                    }
                    break;
                }
                case 'upscale': {
                    const result = await upscaleVideo(await getPublicVideoUrl(), setStatusMsg);
                    if (result.videoUrl) { setResultVideoUrl(result.videoUrl); trackCredits(result); }
                    break;
                }
                case 'relight': {
                    const prompt = relightPreset?.prompt || relightCustom;
                    if (!prompt) { setError('Select a preset or enter a custom prompt.'); break; }
                    const result = await relightVideo(await getPublicVideoUrl(), prompt, setStatusMsg);
                    if (result.videoUrl) { setResultVideoUrl(result.videoUrl); trackCredits(result); }
                    break;
                }
                case 'lipsync': {
                    let audioUrl = lipsyncAudioUrl;
                    if (lipsyncAudioFile && !lipsyncAudioUrl) {
                        audioUrl = await fileToDataUrl(lipsyncAudioFile);
                    }
                    if (!audioUrl) { setError('Upload or provide an audio file.'); break; }
                    const result = await generateLipsync(await getPublicVideoUrl(), audioUrl, setStatusMsg);
                    if (result.videoUrl) { setResultVideoUrl(result.videoUrl); trackCredits(result); }
                    break;
                }
                case 'advancedEdit': {
                    // Nov 2026 — Reference elements are now OPTIONAL. Either a
                    // text prompt OR at least one non-empty element is enough
                    // to run the edit. Empty elements (added but no image
                    // uploaded) still block since they'd be sent as junk.
                    const hasPrompt = !!advancedPrompt?.trim();
                    const hasAnyElement = advancedElements.length > 0;
                    if (!hasPrompt && !hasAnyElement) {
                        setError('Add an edit description or at least one reference element.');
                        break;
                    }
                    const hasEmpty = advancedElements.some(el => el.images.length === 0);
                    if (hasEmpty) { setError('Please upload at least one image for each element (or remove empty ones).'); break; }

                    const userEmail = sessionStorage.getItem('userEmail') || '';

                    // Build reference_images array and composite prompt
                    const referenceImages = [];
                    const elementPrompts = [];

                    for (let i = 0; i < advancedElements.length; i++) {
                        const el = advancedElements[i];
                        const elementNum = i + 1;

                        // Upload each image: local files → GCS, asset URLs pass through
                        for (const img of el.images) {
                            if (img.file) {
                                // Local file — convert to data URI, upload to GCS
                                setStatusMsg(`Uploading element ${elementNum} image to storage...`);
                                const dataUri = await fileToDataUrl(img.file);
                                const gcsUrl = await uploadDataUriToBackend(dataUri, userEmail);
                                console.log(`✅ Element ${elementNum} image uploaded to GCS:`, gcsUrl);
                                referenceImages.push(gcsUrl);
                            } else if (img.previewUrl) {
                                // Already a proper URL (from User Assets)
                                referenceImages.push(img.previewUrl);
                            }
                        }

                        // Build per-element prompt: "change the background with element 1"
                        if (el.description?.trim()) {
                            elementPrompts.push(`${el.description.trim()} with element ${elementNum}`);
                        }
                    }

                    // Compose final prompt: main instructions + element-specific instructions
                    const compositeParts = [];
                    if (advancedPrompt?.trim()) compositeParts.push(advancedPrompt.trim());
                    if (elementPrompts.length > 0) compositeParts.push(elementPrompts.join('. '));
                    const compositePrompt = compositeParts.join('. ');

                    console.log('🎬 Advanced edit — reference_images:', referenceImages, 'prompt:', compositePrompt);

                    const byteplusAssets = buildAdvancedEditByteplusAssets(
                        advancedElements.flatMap(el => el.images.map(img => img.project).filter(Boolean)),
                        projectData?.characters || []
                    );
                    if (byteplusAssets.length) console.log('🎬 Advanced edit — byteplus_assets:', byteplusAssets);

                    const advResult = await editVideoAdvanced(
                        await getPublicVideoUrl(), referenceImages, compositePrompt, setStatusMsg, byteplusAssets, null, null, editOptions
                    );
                    if (advResult.videoUrl) { setResultVideoUrl(advResult.videoUrl); trackCredits(advResult); }
                    break;
                }
                case 'swapObjects': {
                    const req = buildSwapRequest(swapItems, swapReplacements, swapNote);
                    if (req.swaps.length === 0) { setError('Pick a replacement for at least one item.'); break; }
                    console.log('🔁 Swap — swaps:', req.swaps, 'reference_images:', req.referenceImages, 'prompt:', req.prompt);
                    const swapResult = await editVideoAdvanced(
                        await getPublicVideoUrl(), req.referenceImages, req.prompt, setStatusMsg,
                        req.byteplusAssets.length ? req.byteplusAssets : null, req.swaps, req.keepUnchanged, editOptions
                    );
                    if (swapResult.videoUrl) { setResultVideoUrl(swapResult.videoUrl); trackCredits(swapResult); }
                    break;
                }
                case 'voiceChanger': {
                    if (!selectedVoiceCharId) { setError('Select a character segment to change voice.'); break; }
                    const segment = voiceSegments.find(s => s.characterId === selectedVoiceCharId);
                    if (!segment) { setError('Could not find the selected segment.'); break; }
                    if (!segment.voiceId) { setError(`${segment.characterName} does not have a voice ID assigned.`); break; }
                    
                    setStatusMsg(`Changing voice for ${segment.characterName} (${segment.startTime.toFixed(1)}s - ${segment.endTime.toFixed(1)}s)...`);
                    
                    // Ensure we have a proper hosted URL (not a data URI which would be too long)
                    let videoToChange = resultVideoUrl || shot.video;
                    if (videoToChange && (videoToChange.startsWith('data:') || videoToChange.startsWith('blob:'))) {
                        setStatusMsg('Uploading video to storage...');
                        const userEmail = sessionStorage.getItem('userEmail') || '';
                        try {
                            videoToChange = await uploadDataUriToBackend(videoToChange, userEmail);
                        } catch (uploadErr) {
                            setError('Failed to upload video for voice change. URL too large.');
                            break;
                        }
                    }
                    
                    const result = await changeVideoVoiceSegment(
                        videoToChange,
                        segment.voiceId,
                        segment.startTime,
                        segment.endTime,
                        setStatusMsg
                    );
                    if (result.videoUrl) {
                        setResultVideoUrl(result.videoUrl);
                        trackCredits(result);
                        setVoiceSegments(prev => prev.map(s => 
                            s.characterId === selectedVoiceCharId ? { ...s, applied: true } : s
                        ));
                    }
                    break;
                }
                default: {
                    const editParams = { tool: activeTool, videoUrl: shot.video, shotId: shot.id };
                    if (activeTool === 'zoom') { editParams.zoomLevel = zoomLevel; editParams.panX = panX; editParams.panY = panY; }
                    if (activeTool === 'crop') editParams.cropRegion = cropRegion;
                    if (['eraseElement'].includes(activeTool)) { editParams.prompt = editPrompt; }
                    console.log('Applying video edit:', editParams);
                    alert(`Video edit "${activeTool}" will be processed. This feature requires backend integration.`);
                    await new Promise(r => setTimeout(r, 1000));
                }
            }
        } catch (err) {
            console.error('Video edit failed:', err);
            setError(friendlyEditError(err.message) || 'Failed to apply edit');
        } finally {
            setIsProcessing(false); setStatusMsg('');
        }
    };

    const handleSaveResult = () => {
        if (resultVideoUrl && onSave) {
            onSave({ ...shot, video: resultVideoUrl });
            if (onSaveMedia) onSaveMedia([resultVideoUrl]);
        }
        setResultVideoUrl(null);
    };

    // ─── Tool Panels ───
    const renderToolPanel = () => {
        if (!activeTool) {
            return (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <p className="text-sm mb-1">Select a tool to start editing</p>
                    <p className="text-xs">Choose from the tools on the left</p>
                </div>
            );
        }

        switch (activeTool) {
            case 'swapObjects':
                return (
                    <div className="space-y-3">
                    <EditModelSettings model={editModel} setModel={setEditModel} resolution={editResolution} setResolution={setEditResolution} disabled={isProcessing}>{videoAssetField}</EditModelSettings>
                    <SwapObjectsPanel items={swapItems} replacements={swapReplacements} setReplacements={setSwapReplacements}
                        note={swapNote} setNote={setSwapNote} disabled={isProcessing || detectState.busy}
                        source={swapSource} onAnalyse={handleAnalyseVideo} detectState={detectState}
                        onToggleScreenshot={swapSource === 'detected' ? handleToggleScreenshot : null} />
                    </div>
                );
            case 'advancedEdit':
                return (
                    <div className="space-y-3" data-testid="tool-advanced-edit">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Advanced Edit</h3>
                        <p className="text-xs text-gray-500">Describe the edit you want. Reference elements are <span className="text-gray-300">optional</span> — add them only if you want AI to inject specific images/videos into the shot.</p>
                        <EditModelSettings model={editModel} setModel={setEditModel} resolution={editResolution} setResolution={setEditResolution} disabled={isProcessing}>{videoAssetField}</EditModelSettings>

                        {/* General prompt */}
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Edit Instructions</label>
                            <textarea value={advancedPrompt} onChange={e => setAdvancedPrompt(e.target.value)}
                                placeholder="Describe the overall edit you want to apply..."
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-16 text-sm text-gray-200 placeholder-gray-600 focus:border-purple-500/60 focus:outline-none transition-colors" />
                        </div>

                        {/* Elements list */}
                        <div className="space-y-2.5">
                            <div className="flex items-center justify-between">
                                <label className="text-xs text-gray-400 font-medium">Reference Elements ({advancedElements.length})</label>
                            </div>

                            {advancedElements.length === 0 && (
                                <div className="border border-dashed border-gray-700 rounded-lg p-4 text-center">
                                    <p className="text-xs text-gray-600">No elements added yet</p>
                                    <p className="text-[10px] text-gray-700 mt-0.5">Click the button below to add reference images/videos</p>
                                </div>
                            )}

                            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                                {advancedElements.map((el, idx) => (
                                    <div key={el.id} className="bg-gray-800/60 border border-gray-700/80 rounded-lg p-2.5 space-y-2 relative" data-testid={`advanced-element-${idx}`}>
                                        {/* Header row */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Element {idx + 1}</span>
                                            <button onClick={() => removeAdvancedElement(el.id)}
                                                className="p-0.5 rounded hover:bg-red-600/20 text-gray-600 hover:text-red-400 transition-colors"
                                                data-testid={`remove-element-${idx}`}>
                                                <TrashIcon className="w-3.5 h-3.5" />
                                            </button>
                                        </div>

                                        {/* Multi-image upload area */}
                                        <input type="file" accept="image/*,video/*" multiple className="hidden"
                                            ref={ref => { advancedFileInputRefs.current[el.id] = ref; }}
                                            onChange={e => {
                                                handleAdvancedFileSelect(el.id, e.target.files);
                                                e.target.value = '';
                                            }} />

                                        {/* Image thumbnails grid */}
                                        {el.images.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {el.images.map((img, imgIdx) => (
                                                    <div key={imgIdx} className="relative w-14 h-14 rounded-md overflow-hidden border border-gray-700 group/img">
                                                        {img.file?.type?.startsWith('video') ? (
                                                            <video src={img.previewUrl} className="w-full h-full object-cover" muted />
                                                        ) : (
                                                            <img src={img.previewUrl} alt={`El ${idx+1} img ${imgIdx+1}`} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                                                        )}
                                                        <button onClick={() => removeAdvancedImage(el.id, imgIdx)}
                                                            className="absolute top-0 right-0 bg-black/70 hover:bg-red-600 rounded-bl w-4 h-4 flex items-center justify-center text-[8px] text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
                                                            data-testid={`remove-img-${idx}-${imgIdx}`}>x</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Add images buttons */}
                                        <div className="flex gap-1.5">
                                            <button onClick={() => advancedFileInputRefs.current[el.id]?.click()}
                                                className="flex-1 py-2 border border-dashed border-gray-600 rounded-md hover:border-purple-500/60 text-[10px] flex items-center justify-center gap-1 text-gray-500 hover:text-gray-300 transition-colors"
                                                data-testid={`upload-element-${idx}`}>
                                                <PlusCircleIcon className="w-3.5 h-3.5 opacity-50" />
                                                {el.images.length > 0 ? 'Add More' : 'Upload'}
                                            </button>
                                            <button onClick={() => setAssetPickerForElementId(el.id)}
                                                className="flex-1 py-2 border border-dashed border-gray-600 rounded-md hover:border-purple-500/60 text-[10px] flex items-center justify-center gap-1 text-gray-500 hover:text-gray-300 transition-colors"
                                                data-testid={`assets-element-${idx}`}>
                                                <LibraryIcon className="w-3.5 h-3.5 opacity-50" />
                                                From Assets
                                            </button>
                                            <button onClick={() => setProjectPickerForElementId(el.id)}
                                                className="flex-1 py-2 border border-dashed border-gray-600 rounded-md hover:border-purple-500/60 text-[10px] flex items-center justify-center gap-1 text-gray-500 hover:text-gray-300 transition-colors"
                                                data-testid={`project-assets-element-${idx}`}>
                                                <LibraryIcon className="w-3.5 h-3.5 opacity-50" />
                                                Project
                                            </button>
                                        </div>

                                        {/* Description text box */}
                                        <textarea value={el.description}
                                            onChange={e => updateAdvancedElement(el.id, 'description', e.target.value)}
                                            placeholder="What does this replace or where should it appear?"
                                            className="w-full p-1.5 bg-gray-900/60 border border-gray-700/60 rounded-md resize-none h-12 text-xs text-gray-300 placeholder-gray-600 focus:border-purple-500/50 focus:outline-none transition-colors"
                                            data-testid={`element-description-${idx}`} />
                                    </div>
                                ))}
                            </div>

                            {/* Add element button */}
                            <button onClick={addAdvancedElement} data-testid="add-advanced-element-btn"
                                className="w-full py-2 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 text-xs flex items-center justify-center gap-1.5 text-gray-400 hover:text-purple-400 transition-colors">
                                <PlusCircleIcon className="w-4 h-4" /> Add Element
                            </button>
                        </div>
                    </div>
                );

            case 'trim': {
                // Nov 2026 — Metadata trim UI. Two range sliders (start/end)
                // let users define the keep-window in seconds. Playback in
                // the main preview auto-loops within [trimStart, trimEnd]
                // whenever this tool is active.
                const total = duration || 0;
                const end = trimEnd == null ? total : trimEnd;
                const clip = Math.max(0, end - trimStart);
                const step = 0.1;
                const isTrimmed = trimStart > 0.001 || (trimEnd != null && Math.abs(trimEnd - total) > 0.001);
                return (
                    <div className="space-y-4" data-testid="tool-panel-trim">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Trim</h3>
                            <p className="text-xs text-gray-500 mt-1">Pick the segment to keep. The video is cut in your browser and the shorter clip replaces the original — no credits used. Trim takes 15–45s depending on video length.</p>
                        </div>

                        <div className="p-3 bg-gray-900/40 rounded-lg space-y-3">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-500">Original</span>
                                <span className="text-gray-300 font-mono">{total.toFixed(2)}s</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-500">Trimmed clip</span>
                                <span className="text-purple-300 font-mono" data-testid="trim-clip-duration">{clip.toFixed(2)}s</span>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-xs text-gray-500">
                                Start: <span className="text-gray-300 font-mono">{trimStart.toFixed(2)}s</span>
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={total || 0}
                                step={step}
                                value={trimStart}
                                onChange={e => {
                                    const v = Math.min(+e.target.value, (trimEnd ?? total) - 0.5);
                                    setTrimStart(Math.max(0, v));
                                    if (videoRef.current) videoRef.current.currentTime = Math.max(0, v);
                                }}
                                className="w-full accent-purple-500"
                                data-testid="trim-start-slider" />
                        </div>

                        <div className="space-y-2">
                            <label className="block text-xs text-gray-500">
                                End: <span className="text-gray-300 font-mono">{end.toFixed(2)}s</span>
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={total || 0}
                                step={step}
                                value={end}
                                onChange={e => {
                                    const v = Math.max(+e.target.value, trimStart + 0.5);
                                    setTrimEnd(Math.min(total, v));
                                }}
                                className="w-full accent-purple-500"
                                data-testid="trim-end-slider" />
                        </div>

                        {isTrimmed && (
                            <button
                                onClick={() => { setTrimStart(0); setTrimEnd(total); }}
                                className="w-full text-xs text-gray-400 hover:text-purple-400 py-1.5 border border-gray-700/60 rounded-md hover:border-purple-500/40 transition-colors"
                                data-testid="trim-reset-btn">
                                Reset to full length
                            </button>
                        )}

                        {shot?.trimStart != null && shot?.trimEnd != null && (
                            <div className="text-[10px] text-gray-500 bg-gray-900/40 px-2 py-1.5 rounded">
                                Current saved trim: <span className="text-gray-300 font-mono">{shot.trimStart}s → {shot.trimEnd}s</span>
                            </div>
                        )}
                    </div>
                );
            }

            case 'zoom':
                return (
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Zoom & Pan</h3>
                        <div><label className="block text-xs text-gray-500 mb-1">Zoom Level: {zoomLevel}%</label>
                            <input type="range" min="50" max="300" value={zoomLevel} onChange={e => setZoomLevel(+e.target.value)} className="w-full accent-purple-500" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-xs text-gray-500 mb-1">Pan X: {panX}%</label>
                                <input type="range" min="-50" max="50" value={panX} onChange={e => setPanX(+e.target.value)} className="w-full accent-purple-500" /></div>
                            <div><label className="block text-xs text-gray-500 mb-1">Pan Y: {panY}%</label>
                                <input type="range" min="-50" max="50" value={panY} onChange={e => setPanY(+e.target.value)} className="w-full accent-purple-500" /></div>
                        </div>
                        <button onClick={() => { setZoomLevel(100); setPanX(0); setPanY(0); }} className="text-xs text-gray-400 hover:text-white">Reset to default</button>
                    </div>
                );

            case 'crop':
                return (
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Crop Video</h3>
                        <div className="grid grid-cols-2 gap-3">
                            {[{k:'x',l:'X Position',m:50},{k:'y',l:'Y Position',m:50},{k:'width',l:'Width',m:100,n:20},{k:'height',l:'Height',m:100,n:20}].map(f => (
                                <div key={f.k}><label className="block text-xs text-gray-500 mb-1">{f.l}: {cropRegion[f.k]}%</label>
                                    <input type="range" min={f.n||0} max={f.m} value={cropRegion[f.k]} onChange={e => setCropRegion(p => ({...p,[f.k]:+e.target.value}))} className="w-full accent-purple-500" /></div>
                            ))}
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                            <button onClick={() => setCropRegion({x:0,y:0,width:100,height:100})} className="text-xs text-gray-400 hover:text-white">Reset</button>
                            {[['9:16',{x:12.5,y:0,width:75,height:100}],['16:9',{x:0,y:12.5,width:100,height:75}],['1:1',{x:12.5,y:12.5,width:75,height:75}]].map(([l,v])=>(
                                <button key={l} onClick={() => setCropRegion(v)} className="text-xs bg-gray-700 px-2 py-1 rounded hover:bg-gray-600">{l}</button>
                            ))}
                        </div>
                    </div>
                );

            case 'eraseElement':
                return (
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Erase Element</h3>
                        <p className="text-xs text-gray-500">Describe the element to remove from the video.</p>
                        <textarea value={editPrompt} onChange={e => setEditPrompt(e.target.value)} placeholder="e.g., Remove the watermark in the corner..."
                            className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-20 text-sm" />
                    </div>
                );

            // ─── NEW TOOLS ───
            case 'upscale':
                return (
                    <div className="space-y-3" data-testid="tool-upscale">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Upscale Video</h3>
                        <p className="text-xs text-gray-500">Enhance video resolution and detail quality using AI. The content stays the same.</p>
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 space-y-1.5">
                            <div className="flex items-center justify-between text-xs"><span className="text-gray-400">Enhancement</span><span className="text-emerald-400 font-medium">AI Super Resolution</span></div>
                            <div className="flex items-center justify-between text-xs"><span className="text-gray-400">Output</span><span className="text-gray-300">Higher resolution + sharpness</span></div>
                        </div>
                    </div>
                );

            case 'relight':
                return (
                    <div className="space-y-3" data-testid="tool-relight">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Relight Video</h3>
                        <p className="text-xs text-gray-500">Adjust lighting position and mood of the video.</p>
                        <div>
                            <label className="text-xs text-gray-500 mb-1.5 block">Lighting Presets</label>
                            <div className="grid grid-cols-2 gap-1.5">
                                {LIGHTING_PRESETS.map(p => (
                                    <button key={p.id} onClick={() => { setRelightPreset(p); setRelightCustom(''); }}
                                        className={`px-2 py-1.5 rounded-md text-xs font-medium text-left transition-colors ${relightPreset?.id === p.id ? 'bg-amber-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                                        data-testid={`relight-${p.id}`}>{p.label}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Custom Lighting</label>
                            <textarea value={relightCustom}
                                onChange={e => { setRelightCustom(e.target.value); setRelightPreset(null); }}
                                placeholder="e.g., Add sunset rim light from the left..."
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg resize-none h-16 text-sm" />
                        </div>
                    </div>
                );

            case 'lipsync':
                return (
                    <div className="space-y-3" data-testid="tool-lipsync">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Lip Sync</h3>
                        <p className="text-xs text-gray-500">Upload an audio file to sync lip movements in the video.</p>

                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Audio File</label>
                            <input type="file" ref={lipsyncAudioInputRef} accept="audio/*" className="hidden"
                                onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) { setLipsyncAudioFile(file); setLipsyncAudioUrl(URL.createObjectURL(file)); }
                                    e.target.value = '';
                                }} />
                            {lipsyncAudioUrl ? (
                                <div className="relative bg-gray-800 rounded-lg border border-gray-700 p-2">
                                    <audio src={lipsyncAudioUrl} controls className="w-full h-8" />
                                    <button onClick={() => { setLipsyncAudioFile(null); setLipsyncAudioUrl(''); }}
                                        className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] text-white">x</button>
                                </div>
                            ) : (
                                <button onClick={() => lipsyncAudioInputRef.current?.click()} data-testid="lipsync-upload-btn"
                                    className="w-full py-3 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 text-xs flex flex-col items-center gap-1 text-gray-400 hover:text-gray-200 transition-colors">
                                    <LipsyncIcon className="w-5 h-5 opacity-40" /> Upload Audio
                                </button>
                            )}
                        </div>

                        {/* Result video */}
                        {resultVideoUrl && activeTool === 'lipsync' && (
                            <div>
                                <label className="text-xs text-emerald-400 mb-1 block font-medium">Lip Synced Video</label>
                                <video ref={resultVideoRef} src={resultVideoUrl} className="w-full rounded-lg border border-emerald-700" controls autoPlay loop />
                                <button onClick={handleSaveResult} className="w-full mt-1.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg">
                                    Save to Shot
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'voiceChanger': {
                return (
                    <div className="space-y-3" data-testid="tool-voice-changer">
                        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Voice Changer</h3>
                        <p className="text-xs text-gray-500">Change voice for specific character segments. Adjust timecodes and apply per character.</p>
                        
                        {voiceSegments.length === 0 ? (
                            <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-3 text-xs text-amber-400">
                                No dialogue segments found for this shot. Add dialogues in Shot Pre-Production.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {voiceSegments.map((seg, i) => {
                                    const isSelected = selectedVoiceCharId === seg.characterId;
                                    const hasVoice = !!seg.voiceId;
                                    return (
                                        <div key={seg.characterId + '-' + i}
                                            className={`rounded-lg border transition-all ${
                                                isSelected
                                                    ? 'border-violet-500 bg-violet-600/10'
                                                    : seg.applied
                                                        ? 'border-emerald-600/40 bg-emerald-900/10'
                                                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                                            }`}
                                            data-testid={`voice-segment-${seg.characterId}`}
                                        >
                                            {/* Segment header — click to select */}
                                            <button
                                                onClick={() => setSelectedVoiceCharId(isSelected ? null : seg.characterId)}
                                                className="w-full flex items-center gap-2.5 p-2.5 text-left"
                                            >
                                                {seg.image ? (
                                                    <img src={seg.image} alt={seg.characterName} className="w-8 h-8 rounded-full object-cover border border-gray-600 shrink-0" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400 shrink-0">
                                                        {seg.characterName?.[0]}
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-xs font-medium text-gray-200 truncate">{seg.characterName}</span>
                                                        {seg.applied && (
                                                            <span className="text-[9px] bg-emerald-600/30 text-emerald-400 px-1.5 py-0.5 rounded-full font-bold">Applied</span>
                                                        )}
                                                    </div>
                                                    <div className="text-[10px] text-gray-500">
                                                        {hasVoice ? `Voice: ${seg.voiceId.substring(0, 14)}...` : 'No voice assigned'}
                                                    </div>
                                                </div>
                                                {/* Preview segment button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (videoRef.current) {
                                                            videoRef.current.currentTime = seg.startTime;
                                                            videoRef.current.play();
                                                            // Pause at end time
                                                            const checkEnd = () => {
                                                                if (videoRef.current && videoRef.current.currentTime >= seg.endTime) {
                                                                    videoRef.current.pause();
                                                                    videoRef.current.removeEventListener('timeupdate', checkEnd);
                                                                }
                                                            };
                                                            videoRef.current.addEventListener('timeupdate', checkEnd);
                                                        }
                                                    }}
                                                    className="p-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors shrink-0"
                                                    title="Preview this segment"
                                                >
                                                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                                                </button>
                                            </button>

                                            {/* Expanded: timecodes + text */}
                                            {isSelected && (
                                                <div className="px-2.5 pb-2.5 space-y-2">
                                                    {/* Time range */}
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1">
                                                            <label className="text-[9px] text-gray-500 uppercase">Start (s)</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                max={duration}
                                                                value={seg.startTime.toFixed(1)}
                                                                onChange={(e) => setVoiceSegments(prev => prev.map((s, j) =>
                                                                    j === i ? { ...s, startTime: Math.max(0, parseFloat(e.target.value) || 0) } : s
                                                                ))}
                                                                className="w-full p-1.5 bg-gray-900 border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-violet-500"
                                                            />
                                                        </div>
                                                        <span className="text-gray-600 mt-4">—</span>
                                                        <div className="flex-1">
                                                            <label className="text-[9px] text-gray-500 uppercase">End (s)</label>
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                min="0"
                                                                max={duration}
                                                                value={seg.endTime.toFixed(1)}
                                                                onChange={(e) => setVoiceSegments(prev => prev.map((s, j) =>
                                                                    j === i ? { ...s, endTime: Math.min(duration || 999, parseFloat(e.target.value) || 0) } : s
                                                                ))}
                                                                className="w-full p-1.5 bg-gray-900 border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-violet-500"
                                                            />
                                                        </div>
                                                    </div>
                                                    {/* Dialogue text preview */}
                                                    {seg.text && (
                                                        <p className="text-[10px] text-gray-500 italic bg-gray-900/50 rounded p-1.5 line-clamp-2">"{seg.text}"</p>
                                                    )}
                                                    {/* Apply button */}
                                                    {hasVoice ? (
                                                        <button
                                                            onClick={handleApplyEdit}
                                                            disabled={isProcessing}
                                                            className="w-full py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-xs font-semibold rounded-lg transition-colors"
                                                        >
                                                            {isProcessing && selectedVoiceCharId === seg.characterId ? 'Applying...' : `Apply Voice Change (${seg.startTime.toFixed(1)}s - ${seg.endTime.toFixed(1)}s)`}
                                                        </button>
                                                    ) : (
                                                        <p className="text-[10px] text-amber-500">Assign a voice to this character in the Cast & World page to enable voice change.</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Result video */}
                        {resultVideoUrl && activeTool === 'voiceChanger' && (
                            <div>
                                <label className="text-xs text-emerald-400 mb-1 block font-medium">Voice Changed Video</label>
                                <video ref={resultVideoRef} src={resultVideoUrl} className="w-full rounded-lg border border-emerald-700" controls autoPlay loop />
                                <button onClick={handleSaveResult} className="w-full mt-1.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg">
                                    Save to Shot
                                </button>
                            </div>
                        )}
                    </div>
                );
            }

            default: return null;
        }
    };

    // ─── Main Render ───
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="video-editor-overlay">
            <div className="bg-[#12121a] border border-gray-700/60 rounded-xl w-full max-w-[1200px] max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-testid="video-editor-modal">

                {/* Header */}
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-800">
                    <div>
                        <h2 className="text-base font-semibold text-gray-100">Video Editor</h2>
                        <p className="text-xs text-gray-500">Edit and enhance your video</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-700/60 transition-colors" data-testid="video-editor-close"><CloseIcon className="w-5 h-5" /></button>
                </div>

                {/* Body */}
                <div className="flex-1 flex overflow-hidden min-h-0">

                    {/* Tool Sidebar */}
                    <div className="w-[96px] bg-[#0e0e16] border-r border-gray-800 flex flex-col items-center py-2 gap-0.5 flex-shrink-0 overflow-y-auto" data-testid="video-tool-sidebar">
                        {EDIT_TOOLS.map(tool => (
                            <button key={tool.id} onClick={() => { setActiveTool(tool.id); setError(null); setResultVideoUrl(null); }}
                                className={`w-[80px] py-2 rounded-lg flex flex-col items-center gap-1 transition-all text-[10px] font-medium
                                    ${activeTool === tool.id ? 'bg-purple-600/20 text-purple-400 border border-purple-500/40' : 'text-gray-500 hover:bg-gray-800 hover:text-gray-300'}`}
                                data-testid={`video-tool-${tool.id}`}>
                                <tool.icon className="w-5 h-5" />
                                {tool.name}
                            </button>
                        ))}
                    </div>

                    {/* Preview */}
                    <div className="flex-1 flex flex-col min-w-0">
                        <div className="flex-1 flex items-center justify-center bg-[#0a0a12] p-4 relative">
                            {shot.video ? (
                                <div className={showCompare ? 'w-full h-full grid grid-cols-2 gap-4 items-center' : 'contents'} data-testid={showCompare ? 'swap-compare-view' : undefined}>
                                <div className={showCompare ? 'relative flex flex-col items-center min-w-0' : 'relative max-w-full max-h-full'}>
                                    {showCompare && <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5" data-testid="compare-original-label">Original</span>}
                                    <video ref={videoRef} src={shot.video}
                                        className="max-w-full max-h-[55vh] rounded-lg"
                                        style={{
                                            transform: `scale(${zoomLevel / 100}) translate(${panX}%, ${panY}%)`,
                                            clipPath: activeTool === 'crop' ? `inset(${cropRegion.y}% ${100-cropRegion.x-cropRegion.width}% ${100-cropRegion.y-cropRegion.height}% ${cropRegion.x}%)` : 'none'
                                        }}
                                        loop crossOrigin="anonymous" />
                                    {activeTool === 'crop' && (
                                        <div className="absolute border-2 border-purple-500 border-dashed pointer-events-none"
                                            style={{ left: `${cropRegion.x}%`, top: `${cropRegion.y}%`, width: `${cropRegion.width}%`, height: `${cropRegion.height}%` }} />
                                    )}
                                </div>
                                {showCompare && (
                                    <div className="relative flex flex-col items-center min-w-0">
                                        <span className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider mb-1.5" data-testid="compare-edited-label">Edited</span>
                                        <video ref={compareVideoRef} src={resultVideoUrl} className="max-w-full max-h-[55vh] rounded-lg ring-2 ring-emerald-600/60"
                                            loop muted={false} controls playsInline data-testid="compare-edited-video" />
                                    </div>
                                )}
                                </div>
                            ) : (
                                <div className="text-gray-600 text-center">
                                    <p className="text-sm">No video available</p>
                                    <p className="text-xs mt-1">Generate or upload a video first</p>
                                </div>
                            )}
                            {isProcessing && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                                        <span className="text-sm text-gray-300">{statusMsg || 'Processing...'}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Video Controls */}
                        {shot.video && (
                            <div className="px-4 py-3 border-t border-gray-800 bg-[#0e0e16]">
                                <div className="flex items-center gap-3">
                                    <button onClick={handlePlayPause} className="p-2 bg-purple-600 rounded-full hover:bg-purple-700 transition-colors">
                                        {isPlaying ? <PauseIcon className="w-4 h-4" /> : <PlayIcon className="w-4 h-4" />}
                                    </button>
                                    <span className="text-xs text-gray-400 w-10">{formatTime(currentTime)}</span>
                                    <div className="flex-1 h-1.5 bg-gray-700 rounded-full cursor-pointer relative" onClick={handleSeek}>
                                        <div className="absolute h-full bg-purple-500 rounded-full" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                                    </div>
                                    <span className="text-xs text-gray-400 w-10 text-right">{formatTime(duration)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tool Panel */}
                    <div className="w-[280px] bg-[#12121a] border-l border-gray-800 p-4 overflow-y-auto flex-shrink-0" data-testid="video-tool-panel">
                        {renderToolPanel()}

                        {/* Result preview for upscale/relight */}
                        {resultVideoUrl && !['lipsync'].includes(activeTool) && (
                            <div className="mt-3">
                                <label className="text-xs text-emerald-400 mb-1 block font-medium">Result</label>
                                {showCompare
                                    ? <p className="text-[11px] text-gray-400" data-testid="compare-hint">Shown next to the original in the preview. Play the original to watch both in sync.</p>
                                    : <video src={resultVideoUrl} className="w-full rounded-lg border border-emerald-700" controls autoPlay loop />}
                                <button onClick={handleSaveResult} className="w-full mt-1.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg">Save to Shot</button>
                            </div>
                        )}

                        {error && (
                            <div className="mt-3 bg-red-500/10 border border-red-500/40 text-red-400 p-2.5 rounded-lg text-xs" data-testid="video-error">{error}</div>
                        )}

                        {activeTool && (
                            <button onClick={handleApplyEdit} disabled={isProcessing} data-testid="video-apply-btn"
                                className="w-full mt-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                                {isProcessing ? <><LoadingSpinner className="w-4 h-4" /> Processing...</> : <>Apply {EDIT_TOOLS.find(t => t.id === activeTool)?.name}</>}
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-800 flex justify-between items-center">
                    <p className="text-xs text-gray-500">Shot: {shot.title || `Shot ${shot.id?.substring(0, 8)}`}</p>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors" data-testid="video-cancel-btn">Cancel</button>
                        <button onClick={onClose} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors" data-testid="video-done-btn">Done</button>
                    </div>
                </div>
            </div>

            {projectPickerForElementId && (
                <ProjectAssetsPickerModal
                    characters={projectData?.characters || []}
                    onSelect={handleProjectAssetSelected}
                    onClose={() => setProjectPickerForElementId(null)}
                />
            )}

            {/* User Assets Modal */}
            {assetPickerForElementId && (
                <UserAssetsModal
                    activeTab="elements"
                    onSelect={handleAssetSelected}
                    onClose={() => setAssetPickerForElementId(null)}
                />
            )}
        </div>
    );
};
